import type { View } from "@-/com.npmjs.react-native";
import {
  cancelAnimation,
  Easing,
  measure,
  withSpring,
  withTiming,
  type AnimatedRef,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { scheduleOnRN } from "@-/com.npmjs.react-native-worklets";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import { PRIMARY_NAVIGATION_STRIP_EDITOR_CHIP_SCALE } from "#primary-navigation-customization-constants";
import {
  getPrimaryNavigationStripHoverSlot,
  getPrimaryNavigationStripSlotCenter,
  type PrimaryNavigationStripEditorMetrics,
} from "#primary-navigation-strip-editor-geometry";
import {
  arePrimaryNavigationStripOrdersEqual,
  placeInPrimaryNavigationStrip,
  removeFromPrimaryNavigationStrip,
} from "#primary-navigation-strip-order";

export type PrimaryNavigationCustomizationFeedback = "drop" | "hover" | "pickup";

export type PrimaryNavigationCustomizationDrag = {
  destination: PrimaryNavigationDestination;
  /** Slot of the strip the icon would drop into, or -1 while it is off the strip. */
  hoverSlot: number;
  id: number;
  /** Center of the picked-up icon, in ghost-layer coordinates. */
  originX: number;
  originY: number;
  /**
   * `dragging` follows the finger. `settling` animates the ghost onto the result (its new slot,
   * back to where it came from, or away when removed), then the drag clears and the real icon shows.
   */
  phase: "dragging" | "settling";
  /** The editable strip's origin in ghost-layer coordinates; null when no strip was mounted. */
  stripX: number | null;
  stripY: number | null;
};

/** UI-thread state shared by every drag worklet, plus the JS callbacks they schedule. */
export type PrimaryNavigationCustomizationWorklets = {
  /** Destinations offered in this session, in canonical (palette) order. */
  available: SharedValue<readonly PrimaryNavigationDestination[]>;
  drag: SharedValue<PrimaryNavigationCustomizationDrag | null>;
  ghostLayerRef: AnimatedRef<View>;
  ghostOpacity: SharedValue<number>;
  ghostScale: SharedValue<number>;
  /** Ghost center, in ghost-layer coordinates. */
  ghostX: SharedValue<number>;
  ghostY: SharedValue<number>;
  lastDragId: SharedValue<number>;
  onCommit: (order: readonly PrimaryNavigationDestination[]) => void;
  onDraggingChange: (isDragging: boolean) => void;
  onFeedback: (feedback: PrimaryNavigationCustomizationFeedback) => void;
  /** The strip being edited. Every change lands here first, on the UI thread. */
  order: SharedValue<readonly PrimaryNavigationDestination[]>;
  stripMetrics: SharedValue<PrimaryNavigationStripEditorMetrics | null>;
  stripRef: AnimatedRef<View>;
};

/** The ghost grows slightly under the finger so it reads as lifted off the page. */
export const PRIMARY_NAVIGATION_CUSTOMIZATION_LIFTED_SCALE = 1.15;
const PICKUP_SPRING = { damping: 14, mass: 0.5, stiffness: 260 } as const;
const SETTLE_TIMING = { duration: 200, easing: Easing.out(Easing.cubic) } as const;

/**
 * Where a strip icon sits while the strip is edited: its slot among `slotCount`, or null when it
 * is not in the strip. While an icon is dragged over the strip, the others make room at the
 * hovered slot, so the strip previews the drop in place.
 */
export function getPrimaryNavigationStripEditorTileSlot(
  order: readonly PrimaryNavigationDestination[],
  drag: PrimaryNavigationCustomizationDrag | null,
  destination: PrimaryNavigationDestination,
): { slot: number; slotCount: number } | null {
  "worklet";
  if (drag === null || drag.phase !== "dragging") {
    const slot = order.indexOf(destination);
    return slot === -1 ? null : { slot, slotCount: order.length };
  }
  const others = removeFromPrimaryNavigationStrip(order, drag.destination);
  if (destination === drag.destination) {
    return drag.hoverSlot === -1 ? null : { slot: drag.hoverSlot, slotCount: others.length + 1 };
  }
  const index = others.indexOf(destination);
  if (index === -1) {
    return null;
  }
  if (drag.hoverSlot === -1) {
    return { slot: index, slotCount: others.length };
  }
  return {
    slot: index >= drag.hoverSlot ? index + 1 : index,
    slotCount: others.length + 1,
  };
}

/** Position of `destination` among the destinations not in the strip, or -1 when it is in it. */
export function getPrimaryNavigationPaletteSlot(
  available: readonly PrimaryNavigationDestination[],
  order: readonly PrimaryNavigationDestination[],
  destination: PrimaryNavigationDestination,
): number {
  "worklet";
  if (order.includes(destination)) {
    return -1;
  }
  return available.filter((candidate) => !order.includes(candidate)).indexOf(destination);
}

function getHoverSlot(
  state: PrimaryNavigationCustomizationWorklets,
  drag: PrimaryNavigationCustomizationDrag,
  x: number,
  y: number,
): number {
  "worklet";
  const order = state.order.get();
  const isInStrip = order.includes(drag.destination);
  const metrics = state.stripMetrics.get();
  const slot =
    metrics === null || drag.stripX === null || drag.stripY === null
      ? -1
      : getPrimaryNavigationStripHoverSlot(
          metrics,
          { x: x - drag.stripX, y: y - drag.stripY },
          isInStrip ? order.length : order.length + 1,
        );
  // The strip keeps at least one destination: its last icon snaps back instead of leaving.
  return slot === -1 && isInStrip && order.length <= 1 ? order.indexOf(drag.destination) : slot;
}

/**
 * Picks up `destination`. `origin` is the touched icon's center in page coordinates; omit it for a
 * strip icon, whose center follows from its slot. Returns the drag id, or -1 when the drag could
 * not start (another finger is already dragging, or the ghost layer is not laid out).
 */
export function startPrimaryNavigationCustomizationDrag(
  state: PrimaryNavigationCustomizationWorklets,
  destination: PrimaryNavigationDestination,
  origin: { pageX: number; pageY: number } | null,
): number {
  "worklet";
  const current = state.drag.get();
  if (current !== null && current.phase === "dragging") {
    return -1;
  }
  const layer = measure(state.ghostLayerRef);
  if (layer === null) {
    return -1;
  }
  const strip = measure(state.stripRef);
  const stripX = strip === null ? null : strip.pageX - layer.pageX;
  const stripY = strip === null ? null : strip.pageY - layer.pageY;
  const order = state.order.get();
  const metrics = state.stripMetrics.get();
  const slot = order.indexOf(destination);
  let originX: number;
  let originY: number;
  if (origin !== null) {
    originX = origin.pageX - layer.pageX;
    originY = origin.pageY - layer.pageY;
  } else if (slot !== -1 && metrics !== null && stripX !== null && stripY !== null) {
    const center = getPrimaryNavigationStripSlotCenter(metrics, slot, order.length);
    originX = stripX + center.x;
    originY = stripY + center.y;
  } else {
    return -1;
  }

  // A previous drop may still be settling; this pickup supersedes it (its id no longer matches).
  cancelAnimation(state.ghostX);
  cancelAnimation(state.ghostY);
  cancelAnimation(state.ghostScale);
  cancelAnimation(state.ghostOpacity);
  const id = state.lastDragId.get() + 1;
  state.lastDragId.set(id);
  state.ghostX.set(originX);
  state.ghostY.set(originY);
  state.ghostOpacity.set(1);
  // Start at the picked-up chip's own size so the lift grows out of it.
  state.ghostScale.set(origin === null ? PRIMARY_NAVIGATION_STRIP_EDITOR_CHIP_SCALE : 1);
  state.ghostScale.set(withSpring(PRIMARY_NAVIGATION_CUSTOMIZATION_LIFTED_SCALE, PICKUP_SPRING));
  state.drag.set({
    destination,
    hoverSlot: slot,
    id,
    originX,
    originY,
    phase: "dragging",
    stripX,
    stripY,
  });
  scheduleOnRN(state.onDraggingChange, true);
  scheduleOnRN(state.onFeedback, "pickup");
  return id;
}

export function updatePrimaryNavigationCustomizationDrag(
  state: PrimaryNavigationCustomizationWorklets,
  id: number,
  translationX: number,
  translationY: number,
): void {
  "worklet";
  const drag = state.drag.get();
  if (drag === null || drag.id !== id || drag.phase !== "dragging") {
    return;
  }
  const x = drag.originX + translationX;
  const y = drag.originY + translationY;
  state.ghostX.set(x);
  state.ghostY.set(y);
  const hoverSlot = getHoverSlot(state, drag, x, y);
  if (hoverSlot !== drag.hoverSlot) {
    state.drag.set({ ...drag, hoverSlot });
    scheduleOnRN(state.onFeedback, "hover");
  }
}

/**
 * Ends drag `id`. A drop over the strip places the icon at the hovered slot; a strip icon dropped
 * off the strip is removed (never the last one). A cancelled gesture (`didDrop` false) changes
 * nothing. Safe to call repeatedly: only the first call for a dragging `id` acts.
 */
export function endPrimaryNavigationCustomizationDrag(
  state: PrimaryNavigationCustomizationWorklets,
  id: number,
  didDrop: boolean,
): void {
  "worklet";
  const drag = state.drag.get();
  if (drag === null || drag.id !== id || drag.phase !== "dragging") {
    return;
  }
  const order = state.order.get();
  const wasInStrip = order.includes(drag.destination);
  let next = order;
  if (didDrop && drag.hoverSlot !== -1) {
    next = placeInPrimaryNavigationStrip(order, drag.destination, drag.hoverSlot);
  } else if (didDrop && wasInStrip && order.length > 1) {
    next = removeFromPrimaryNavigationStrip(order, drag.destination);
  }
  const didChange = !arePrimaryNavigationStripOrdersEqual(order, next);
  // The order and the end of the drag preview change in the same UI frame, so the strip goes
  // straight from "room made at the slot" to "icon in the slot" without an intermediate layout.
  state.order.set(next);
  state.drag.set({ ...drag, phase: "settling" });

  const finish = () => {
    "worklet";
    // A newer pickup owns the ghost now; leave its state alone.
    if (state.drag.get()?.id !== id) {
      return;
    }
    state.drag.set(null);
    state.ghostOpacity.set(0);
    state.ghostScale.set(1);
    scheduleOnRN(state.onDraggingChange, false);
  };

  const slot = next.indexOf(drag.destination);
  const metrics = state.stripMetrics.get();
  if (slot !== -1 && metrics !== null && drag.stripX !== null && drag.stripY !== null) {
    // Land in the strip slot the real icon now occupies, shrinking to the strip chip's size.
    const center = getPrimaryNavigationStripSlotCenter(metrics, slot, next.length);
    state.ghostScale.set(withTiming(PRIMARY_NAVIGATION_STRIP_EDITOR_CHIP_SCALE, SETTLE_TIMING));
    state.ghostY.set(withTiming(drag.stripY + center.y, SETTLE_TIMING));
    // Clear on any completion, including an interrupted one, so the real icon always reappears.
    state.ghostX.set(withTiming(drag.stripX + center.x, SETTLE_TIMING, finish));
  } else if (wasInStrip && slot === -1) {
    // Removed: the icon lifts away; the palette shows it again once the ghost is gone.
    state.ghostOpacity.set(withTiming(0, SETTLE_TIMING, finish));
    state.ghostScale.set(withTiming(0.6, SETTLE_TIMING));
  } else {
    // Not placed: return to where it was picked up (only a palette tile ends up here).
    state.ghostScale.set(withTiming(1, SETTLE_TIMING));
    state.ghostY.set(withTiming(drag.originY, SETTLE_TIMING));
    state.ghostX.set(withTiming(drag.originX, SETTLE_TIMING, finish));
  }

  if (didChange) {
    scheduleOnRN(state.onCommit, next);
    scheduleOnRN(state.onFeedback, "drop");
  }
}

/** Abandons any drag immediately, e.g. when the edit session ends under the finger. */
export function cancelPrimaryNavigationCustomizationDrag(
  state: PrimaryNavigationCustomizationWorklets,
): void {
  "worklet";
  if (state.drag.get() === null) {
    return;
  }
  cancelAnimation(state.ghostX);
  cancelAnimation(state.ghostY);
  cancelAnimation(state.ghostScale);
  cancelAnimation(state.ghostOpacity);
  state.drag.set(null);
  state.ghostOpacity.set(0);
  state.ghostScale.set(1);
  scheduleOnRN(state.onDraggingChange, false);
}
